/**
 * Per-tool section shells — each renders a SectionTabs subheader for its tool
 * and an <Outlet/> for the active sub-page. Used as the element of each tool's
 * PARENT route in App.tsx; the tool's sub-pages are child routes.
 *
 * Tab visibility is derived from the SAME role sources the rest of the app uses
 * (`state.isAccounting`/`isAdmin`, `useIsInvoiceApprover`, `useIsCreditCardApprover`)
 * so the Approvals tabs follow the caller's real roles automatically.
 */

import { Outlet, useLocation, Link } from 'react-router-dom';
import { useProfile } from '../profile/ProfileProvider';
import { useIsInvoiceApprover } from '../invoices/hooks/useIsInvoiceApprover';
import { useIsCreditCardApprover } from '../creditCards/hooks/useIsCreditCardApprover';
import { useIsCommissionsAdmin } from '../commissions/useIsCommissionsAdmin';
import { useCommissionAccess } from '../commissions/useCommissionAccess';
import { SectionTabs, TabRow, type SectionTab } from './SectionTabs';
import { segGroup, segItemOff, segItemOn } from '../design/ui';
import { SUBSCRIPTION_NAV } from '../subscriptions/registry';
import { SubscriptionsRefresh } from '../subscriptions/SubscriptionsRefresh';
import { isSubscriptionAdmin, canViewSubscriptionPage } from '@daaily/shared';

/** Personal/Team split (Aug 2026): approvers get a Team sidebar child that
 *  carries the Approvals queue; the body tab row shows only the active side. */
export function ExpensesSection() {
  const { state } = useProfile();
  const { pathname } = useLocation();
  const canApprove =
    state.status === 'ready' && (state.isAccounting || state.isAdmin);
  const admin = pathname.startsWith('/expenses/approvals');
  const team = pathname.startsWith('/expenses/team');
  const tabs: SectionTab[] = admin && canApprove
    ? [{ to: '/expenses/approvals', label: 'Approvals' }]
    : team
      ? [{ to: '/expenses/team', label: 'Team' }]
      : [
          { to: '/expenses', label: 'Statements', end: true },
          { to: '/expenses/resources', label: 'Resources' },
          { to: '/expenses/allowances', label: 'Allowances' },
        ];
  return <SectionTabs title="Expense Tool" tabs={tabs} />;
}

export function InvoicesSection() {
  const isInvoiceApprover = useIsInvoiceApprover();
  const { pathname } = useLocation();
  const team = pathname.startsWith('/invoices/approvals');
  const tabs: SectionTab[] = team && isInvoiceApprover
    ? [{ to: '/invoices/approvals', label: 'Approvals' }]
    : [
        { to: '/invoices', label: 'My submissions', end: true },
        { to: '/invoices/resources', label: 'Resources' },
      ];
  return <SectionTabs title="Invoice Submission" tabs={tabs} />;
}

export function CommissionsSection() {
  const isAdmin = useIsCommissionsAdmin();
  const { hasAgentLayer, hasBdLayer, hasOtherLayer } = useCommissionAccess();
  const { pathname } = useLocation();
  const layer: 'bd' | 'agents' | 'other' = pathname.startsWith('/commissions/agents') ? 'agents' : pathname.startsWith('/commissions/other') ? 'other' : 'bd';

  // BD tabs (admins) + Agent tabs (admins vs agent/manager) per layer.
  const bdTabs: SectionTab[] = isAdmin ? [
    { to: '/commissions', label: 'Report', end: true },
    { to: '/commissions/statement', label: 'Statements & team' },
    { to: '/commissions/payout', label: 'Payout' },
    { to: '/commissions/accrual', label: 'Accrual & Payout' },
    { to: '/commissions/config', label: 'Configuration' },
  ] : [];
  const agentTabs: SectionTab[] = isAdmin ? [
    { to: '/commissions/agents', label: 'Report', end: true },
    { to: '/commissions/agents/statement', label: 'Statements' },
    { to: '/commissions/agents/payout', label: 'Payout' },
    { to: '/commissions/agents/config', label: 'Configuration' },
  ] : [{ to: '/commissions/agents/statement', label: 'Statements', end: true }];

  // Other layer (person-specific bonus plans): admins run it; the person/manager only see Statements.
  const otherTabs: SectionTab[] = isAdmin ? [
    { to: '/commissions/other', label: 'Report', end: true },
    { to: '/commissions/other/statement', label: 'Statements' },
    { to: '/commissions/other/config', label: 'Configuration' },
  ] : [{ to: '/commissions/other/statement', label: 'Statements', end: true }];

  const layersAvailable = [hasBdLayer, hasAgentLayer, hasOtherLayer].filter(Boolean).length;
  const showToggle = layersAvailable > 1;
  const tabs = layer === 'agents' ? agentTabs : layer === 'other' ? otherTabs : bdTabs;

  // Pure BD non-admin (no agent / other layer) keeps the old bare statement view.
  if (!isAdmin && !hasAgentLayer && !hasOtherLayer) return <Outlet />;

  return (
    <>
      {(showToggle || tabs.length > 0) && (
        <div className="w-full max-w-page px-4 md:px-8 pt-6 space-y-3">
          {showToggle && (
            <div className={segGroup}>
              {([
                ...(hasBdLayer ? [{ to: '/commissions', label: 'BD Employees', on: layer === 'bd' }] : []),
                ...(hasAgentLayer ? [{ to: '/commissions/agents/statement', label: 'Agents', on: layer === 'agents' }] : []),
                ...(hasOtherLayer ? [{ to: '/commissions/other/statement', label: 'Other', on: layer === 'other' }] : []),
              ]).map((l) => (
                <Link key={l.to} to={l.to} aria-current={l.on ? 'page' : undefined} className={l.on ? segItemOn : segItemOff}>
                  {l.label}
                </Link>
              ))}
            </div>
          )}
          {tabs.length > 0 && <div><TabRow tabs={tabs} /></div>}
        </div>
      )}
      <Outlet />
    </>
  );
}

export function CreditCardsSection() {
  const isCreditCardApprover = useIsCreditCardApprover();
  const { pathname } = useLocation();
  const team = pathname.startsWith('/credit-cards/approvals');
  const tabs: SectionTab[] = team && isCreditCardApprover
    ? [{ to: '/credit-cards/approvals', label: 'Approvals' }]
    : [
        { to: '/credit-cards', label: 'My submissions', end: true },
        { to: '/credit-cards/library', label: 'Library' },
      ];
  return <SectionTabs title="Credit Card Submissions" tabs={tabs} />;
}

/** Subscriptions — Revenue (charge revenue) | Health (subscription-base health). */
export function AnalyticsSection() {
  // Nav lives in the SIDEBAR (Traffic | Channels sub-items, like Time Tracking).
  return <Outlet />;
}

export function OperationsSection() {
  // Nav lives in the SIDEBAR (registry-driven sub-items, like Analytics).
  return <Outlet />;
}

export function SubscriptionsSection() {
  // Tabs derive from the registry, filtered by per-sub-nav access (deny-by-default
  // for viewers; admins see all; the Access tab is app-admin only).
  const { state } = useProfile();
  const ready = state.status === 'ready';
  const subAdmin = ready && isSubscriptionAdmin({ isAppAdmin: state.isAdmin, role: state.user.tools?.subscriptions?.role });
  const appAdmin = ready && state.isAdmin;
  const grants = ready ? (state.user.subscriptionPages ?? []) : [];
  const hasAnalytics = ready && state.user.tools?.analytics?.enabled === true;
  const tabs: SectionTab[] = SUBSCRIPTION_NAV
    .filter((d) => !d.hidden)
    .filter((d) => !d.requiresAnalytics || hasAnalytics)
    .filter((d) => (d.admin ? appAdmin : canViewSubscriptionPage(subAdmin, grants, d.key)))
    .map((d) => ({ to: d.path ? `/subscriptions/${d.path}` : '/subscriptions', label: d.label, end: d.path === '' }));
  return (
    <>
      <div className="w-full max-w-page px-4 md:px-8 pt-6">
        <TabRow tabs={tabs} />
      </div>
      {/* One "Refresh data" for every tab (admins) + the reload signal pages listen to. */}
      <SubscriptionsRefresh>
        <Outlet />
      </SubscriptionsRefresh>
    </>
  );
}
